import React, { useState, useEffect } from "react";
import { Server, ShieldCheck, Network, Warehouse, Briefcase, Cpu, Laptop } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
const timelineEvents = [{
  year: "Presente - 2025",
  title: "Founder B-Tech",
  description: "Avvio di un’attività di consulenza indipendente finalizzata a fornire competenze tecniche dirette alle aziende, garantendo un approccio operativo orientato all’affidabilità e alla continuità dei servizi IT.",
  icon: Briefcase,
  tech: ["Consulenza Strategica", "Project Management", "Audit"]
}, {
  year: "2024 - 2022",
  title: "Workplace Services Specialist & Manager",
  description: "Responsabilità nella gestione dei servizi Workplace e del supporto IT in ambienti enterprise distribuiti, supportando utenti, sedi produttive e uffici internazionali.\n\n\nCoordinamento delle attività operative con team interni e partner di supporto di primo e secondo livello, garantendo continuità dei servizi e allineamento alle esigenze del business.\n\n\nPartecipazione alla definizione e implementazione dei processi ITSM, introducendo modelli strutturati per incident, problem, change e request management e migliorando la tracciabilità e l’efficienza del supporto.\n\n\nSupervisione della gestione del ciclo di vita dei dispositivi end-user e delle attività di asset management, contribuendo al miglioramento della governance e dell’efficienza operativa.",
  icon: Warehouse,
  tech: ["Zebra", "Honeywell", "IoT"]
}, {
  year: "2020",
  title: "IT Infrastructure Operations & Team Lead",
  description: "Responsabile delle operations infrastrutturali e del supporto IT in ambienti logistici ad alta intensità operativa.\nCoordinamento di team tecnici e gestione delle attività di supporto su più sedi, con responsabilità nella gestione degli incidenti critici e nel mantenimento della stabilità dei sistemi.\n\n\nCollaborazione costante con il management operativo e con le principali funzioni aziendali per garantire la continuità dei servizi IT e l’allineamento delle soluzioni tecnologiche alle esigenze delle operations.\n\n\nPartecipazione a progetti di evoluzione e aggiornamento dell’infrastruttura, contribuendo al miglioramento dell’affidabilità dei sistemi e dei processi operativi",
  icon: Laptop,
  tech: ["Microsoft 365", "Teams", "Technology Integration"]
}, {
  year: "2022 - 2019",
  title: "IT Infrastructure Deployment Lead\xA0",
  description: "Responsabile del coordinamento tecnico per l’avvio di nuove infrastrutture operative e siti logistici.\nGestione end-to-end delle attività di build IT dalla consegna del sito fino al go-live, coordinando fornitori, team tecnici e contractor per garantire la piena readiness tecnologica delle infrastrutture.\n\n\nSupervisione dell’installazione e configurazione delle componenti infrastrutturali, incluse reti, sistemi wireless, dispositivi end-user e piattaforme di sicurezza, assicurando l’allineamento agli standard operativi e alle tempistiche di progetto.",
  icon: ShieldCheck,
  tech: ["Endpoint Protection", "Disaster Recovery", "GDPR"]
}, {
  year: "2019 – 2018",
  title: "End-User Devices & Logistics Operations Lead",
  description: "Gestione di un team tecnico responsabile del supporto multi-sito e della distribuzione su larga scala di dispositivi end-user utilizzati in ambienti logistici.\nProgettazione dei processi di provisioning e automazione delle attività di configurazione e deployment per la gestione di grandi volumi di dispositivi operativi.\nGestione e ottimizzazione del flusso RMA per il ciclo di sostituzione e riparazione dei dispositivi, garantendo continuità operativa nelle sedi distribuite.\nContributo come Subject Matter Expert allo sviluppo di soluzioni tecniche e modelli operativi per la gestione del ciclo di vita delle tecnologie end-user.",
  icon: Network,
  tech: ["Cisco", "Firewalling", "VPN"]
}, {
  year: "2019 – 2012",
  title: "IT Infrastructure Support Specialist",
  description: "Supporto infrastrutturale e gestione operativa dei sistemi IT in ambienti ad alta intensità operativa, con attività di troubleshooting, gestione dispositivi e continuità del servizio.",
  icon: Server,
  tech: ["Windows Server", "Linux", "VMware"]
}];
function TimelineItem({
  event,
  index,
  isVisible
}: {
  event: any;
  index: number;
  isVisible: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const isEven = index % 2 === 0;
  useEffect(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      setIsOpen(true);
    }
  }, []);
  return <div data-index={index} className={`relative w-full flex flex-col md:flex-row items-start md:items-center ${isEven ? 'md:flex-row-reverse' : ''}`}>
      <div className="hidden md:block md:w-1/2" />

      <div className={`absolute left-4 md:left-1/2 -translate-x-1/2 flex items-center justify-center w-3 h-3 md:w-4 md:h-4 rounded-full bg-background border-2 border-primary z-20 shadow-[0_0_10px_rgba(var(--primary),0.5)] mx-0 md:mb-0`}>
        <div className={`w-1.5 h-1.5 rounded-full bg-primary transition-opacity duration-500 delay-${index * 100 + 500} ${isVisible ? 'opacity-100' : 'opacity-100 md:opacity-0'}`} />
      </div>

      <div className={`w-full md:w-1/2 pl-8 md:pl-0 mx-0 md:mx-0`}>
        <div className={`transition-all duration-700 ease-out transform whitespace-pre-line w-full ${isEven ? 'md:pr-12' : 'md:pl-12'} ${isVisible ? 'opacity-100 translate-y-0 md:translate-x-0' : 'opacity-100 md:opacity-0 translate-y-0 md:translate-y-10'}`} style={{
        transitionDelay: `${index * 150}ms`
      }}>
          <Collapsible open={isOpen} onOpenChange={setIsOpen}>
            <Card className="bg-card/5 backdrop-blur-sm border-border/50 hover:border-primary/50 transition-colors duration-300 group">
              <div className="absolute top-0 right-0 p-4 opacity-5 md:opacity-10 group-hover:opacity-20 transition-opacity">
                <event.icon className="w-16 h-16 text-primary" />
              </div>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-2xl font-mono font-bold text-primary">{event.year}</span>
                  <div className="h-px flex-1 bg-border/50 mx-4" />
                </div>
                <CardTitle className="text-xl text-foreground group-hover:text-primary transition-colors">
                  {event.title}
                </CardTitle>
                <CollapsibleTrigger asChild>
                  <Button variant="outline" size="sm" className="mt-4 w-fit">
                    Scopri di più
                  </Button>
                </CollapsibleTrigger>
              </CardHeader>
              <CollapsibleContent>
                <CardContent>
                  <CardDescription className="text-base mb-4 text-muted-foreground whitespace-pre-line">
                    {event.description}
                  </CardDescription>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>
        </div>
      </div>
    </div>;
}
export default function TenYearsOfItChallenges() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="10-anni-di-sfide-it" className="dark bg-background text-foreground py-20 relative overflow-hidden" ref={ref}>
      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="inline-flex items-center justify-center p-2 bg-primary/10 rounded-full mb-4 border border-primary/20">
            <Cpu className="w-4 h-4 text-primary mr-2" />
            <span className="text-xs font-mono text-primary uppercase tracking-wider">Timeline Esperienza</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6 tracking-tight">
            10 Anni di <span className="text-primary">Sfide IT</span> e Soluzioni
          </h2>
          <p className="text-muted-foreground text-lg">L’esperienza alla base di B-Tech nasce da oltre dieci anni di attività professionale nel settore delle infrastrutture IT, delle operations tecnologiche e del service delivery in contesti enterprise e industriali.<div>Prima della fondazione della società, il fondatore ha ricoperto ruoli tecnici e manageriali in ambienti multinazionali, contribuendo alla gestione di infrastrutture IT complesse, alla guida di team operativi e alla realizzazione di progetti infrastrutturali su larga scala.</div></p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          <div className="absolute left-4 md:left-1/2 -translate-x-1/2 w-0.5 bg-border h-full" />

          <div className="space-y-12 md:space-y-0">
            {timelineEvents.map((event, index) => <TimelineItem key={index} event={event} index={index} isVisible={isVisible} />)}
          </div>
        </div>

        <div className="mt-20 text-center">
          <div className={`transition-all duration-700 delay-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h3 className="text-2xl font-bold mb-6">Vuoi migliorare la tua infrastruttura IT?</h3>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_rgba(var(--primary),0.3)]">
                <Link to="/ContactsPage">
                  Parla con un Consulente
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary/20 hover:bg-primary/10 hover:text-primary">
                <Link to="/ExperiencePage">
                  Approfondisci il Metodo
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}